import { WageFrequency } from "@api/shared-runtime/enums";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isCustomWageFrequency } from "@/features/time-entry/utils/custom-wage";
import {
  getVisibleWageFields,
  isOneTimeFrequency,
  type WageFieldKey,
  type WageFormErrors,
  type WageFormValues,
} from "../utils/wage-fields";

type WageFieldsProps = {
  values: WageFormValues;
  errors: WageFormErrors;
  onChange: (values: WageFormValues) => void;
};

/** Frequency selector plus the wage inputs; per-shift pay has no overtime. */
export function WageFields({ values, errors, onChange }: WageFieldsProps) {
  const visibleFields = getVisibleWageFields(values.wageFrequencyId);
  const layoutClass = isOneTimeFrequency(values.wageFrequencyId)
    ? "grid-cols-1"
    : "grid-cols-1 sm:grid-cols-2";

  function handleFrequencyChange(value: string) {
    if (isCustomWageFrequency(value)) {
      onChange({ ...values, wageFrequencyId: value });
    }
  }

  function handleAmountChange({
    key,
    amount,
  }: {
    key: WageFieldKey;
    amount: string;
  }) {
    onChange({ ...values, amounts: { ...values.amounts, [key]: amount } });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="wage-frequency">Pay frequency</Label>
        <Select
          value={values.wageFrequencyId}
          onValueChange={handleFrequencyChange}
        >
          <SelectTrigger id="wage-frequency">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={WageFrequency.Hourly}>Hourly</SelectItem>
            <SelectItem value={WageFrequency.OneTime}>Per shift</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className={`grid gap-4 ${layoutClass}`}>
        {visibleFields.map((field) => {
          const inputId = `wage-${field.key}`;
          const error = errors[field.key];
          return (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={inputId}>
                {field.label}
                {field.key === "regularWage" && (
                  <span className="text-red-500"> *</span>
                )}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  $
                </span>
                <Input
                  id={inputId}
                  type="text"
                  inputMode="decimal"
                  className="pl-7"
                  placeholder="0.00"
                  value={values.amounts[field.key]}
                  onChange={(event) =>
                    handleAmountChange({
                      key: field.key,
                      amount: event.target.value,
                    })
                  }
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? `${inputId}-error` : undefined}
                />
              </div>
              {error && (
                <p id={`${inputId}-error`} className="text-sm text-red-600">
                  {error}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
